import { IGizmoEvent, IRealmFilledForm, IRealmFormGizmo } from '../../../types';
import FlowyGlobal from '../../flowy-global';
import clone from 'clone';
import { gnov, snov } from '../../../utils';
import downloadRootGizmos from './download-root-gizmos';
import getRootGizmoFidsToDownload from './get-root-gizmo-fids-to-download';
import syncFormOptions from './sync-form-options';

interface IParams {
  type: 'start' | 'forms' | 'filledForms' | 'locations' | 'finish';
  allRegistersUploaded?: boolean;
  context: {
    global: FlowyGlobal;
  };
}

/**
 * During synchronization this function will be called in order to update the sync object
 * that's going to be listened to on the platform layer
 *
 * @param type we're going to specify what part of the sync object we're going to update
 */
async function updateSyncObject({
  type,
  allRegistersUploaded,
  context,
}: IParams) {
  const { global } = context;
  // Sync object
  const sync = clone(global.getSync());
  const database = global.getDatabase();
  const user = await global.getPlatformInterface()?.getUser();
  const apiUrl = global.getApiUrl();

  if (type === 'start' && database && user) {
    // We're going to check which forms need to be downloaded
    const {
      fidsToDownload,
      fidsToRemove,
      pingResponse,
    } = await getRootGizmoFidsToDownload({
      apiUrl,
      database,
      user,
    });
    // TODO: check wich registers need to be downloaded
    console.log('|--------fidsToDownload', fidsToDownload);
    console.log('|--------fidsToRemove', fidsToRemove);

    // TODO: check which registers need to be uploaded
    let savedRegisters: IRealmFilledForm[] = await database.search({
      type: 'filledForms',
      params: {},
    });
    savedRegisters = savedRegisters.filter(
      r => r.offlineStatus !== 'savedUploaded'
    );

    // TODO: check with locations and user events need to be uploaded
    const savedUserEvents = await database.search({
      type: 'userEvents',
      params: {},
    });
    const savedLocations = await database.search({
      type: 'locations',
      params: {},
    });

    snov(sync, 'synchronizing', true);
    snov(sync, 'status.rootGizmoConfigs.fidsToDownload', fidsToDownload);
    snov(sync, 'status.rootGizmoConfigs.fidsToRemove', fidsToRemove);
    snov(sync, 'status.totalRegistersToDownload', savedRegisters.length);
    snov(sync, 'status.pingResponse', pingResponse);

    const progressBar = {
      total:
        fidsToDownload.length +
        savedLocations.length +
        savedUserEvents.length +
        savedRegisters.length,
      progress: 0,
    };

    snov(sync, 'progressBar', progressBar);

    global.setSync(sync);
  } else if (type === 'finish' && database && user) {
    // We're going to download the forms that need to be downloaded
    const rootGizmoFidsToDownload = gnov(
      sync,
      'status.rootGizmoConfigs.fidsToDownload'
    );
    const rootGizmoFidsToRemove = gnov(
      sync,
      'status.rootGizmoConfigs.fidsToRemove'
    );
    const totalRegistersToDownload = gnov(
      sync,
      'status.totalRegistersToDownload'
    );
    const pingResponse: any = gnov(sync, 'status.pingResponse');

    await downloadRootGizmos({
      fidsToDownload: rootGizmoFidsToDownload,
      fidsToRemove: rootGizmoFidsToRemove,
      sync,
      global,
    });

    try {
      const newSavedForms: IRealmFormGizmo[] = await database.search({
        type: 'forms',
        params: {
          //? If there exists a scenario were the same form configuration will have differences between users
          //? we have to add the userId to the search params
          // userId: user.id,
        },
      });

      await syncFormOptions({
        pingResponseData: pingResponse,
        savedForms: newSavedForms,
        allRegistersUploaded,
        context: {
          apiUrl,
          user,
          database,
        },
      });
    } catch (e) {}

    // Right now we're clearing the object without checking for errors
    snov(sync, 'progressBar', undefined);
    snov(sync, 'status.rootGizmoConfigs', undefined);
    snov(sync, 'status.totalRegistersToDownload', undefined);
    snov(sync, 'synchronizing', false);
    snov(sync, 'status.pingResponse', undefined);

    if (
      rootGizmoFidsToDownload &&
      rootGizmoFidsToRemove &&
      (rootGizmoFidsToDownload.length > 0 ||
        rootGizmoFidsToRemove.length > 0 ||
        totalRegistersToDownload > 0) &&
      database.getType() !== 'memory'
    ) {
      // When we have finished syncing we're going to send an event so gizmos can update
      // accordingly. Right now it's only the Index gizmo that's displaying forms who's
      // being updated.
      const sycnFinishedEvent: IGizmoEvent = {
        type: 'global.operation',
        src: {
          type: 'flowy-global',
        },
        data: {
          globalOperation: {
            type: 'sync.finished',
            rootGizmosDownloaded: rootGizmoFidsToDownload.length,
            rootGizmosRemoved: rootGizmoFidsToRemove.length,
          },
        },
      };

      console.log('------------->syncFinished.event');
      await global.sendEvent({ event: sycnFinishedEvent });
    }

    global.setSync(sync);
  }
}

export default updateSyncObject;
